import { type ControlState, NO_CONTROLS } from "../../sim";

/** The parts of the Gamepad API we read (lets tests pass plain objects). */
export interface PadSnapshot {
	connected: boolean;
	mapping: string;
	axes: readonly number[];
	buttons: readonly { pressed: boolean; value: number }[];
}

const STICK_DEADZONE = 0.35;
const TRIGGER_THRESHOLD = 0.3;

/**
 * W3C "standard" gamepad layout (https://w3c.github.io/gamepad/#remapping):
 * buttons 0 A/Cross, 1 B/Circle, 2 X/Square, 4 LB, 5 RB, 6 LT, 7 RT, 12-15 d-pad up/down/left/right;
 * axes 0/1 left stick x/y.
 *
 * thrust: A, RT or d-pad up / stick up · turn: left stick or d-pad · laser: X or RB · missile: B or LB.
 */
export function controlsFromPad(pad: PadSnapshot | null | undefined): ControlState {
	if (!pad?.connected) return { ...NO_CONTROLS };
	const btn = (i: number) => {
		const b = pad.buttons[i];
		return b !== undefined && (b.pressed || b.value > TRIGGER_THRESHOLD);
	};
	const x = pad.axes[0] ?? 0;
	const y = pad.axes[1] ?? 0;
	return {
		thrust: btn(0) || btn(7) || btn(12) || y < -STICK_DEADZONE,
		left: btn(14) || x < -STICK_DEADZONE,
		right: btn(15) || x > STICK_DEADZONE,
		fireLaser: btn(2) || btn(5),
		fireMissile: btn(1) || btn(4),
	};
}

/**
 * Connected pads in connection order: the first pad drives player 1 (blue), the second
 * player 2 (red). Returns an empty list where the Gamepad API is unavailable.
 */
export function connectedPads(): PadSnapshot[] {
	if (typeof navigator === "undefined" || !navigator.getGamepads) return [];
	return navigator
		.getGamepads()
		.filter((p): p is Gamepad => p?.connected === true)
		.sort((a, b) => a.index - b.index);
}
